import { useEffect, useRef, useCallback } from 'react';
import { Article, ArticleVariant, PaginatedResponse } from '@repo/types';
import { Pagination } from '../../../siteStructure/components/Pagination';
import { ListEmptyState } from '../../../common/components/ListEmptyState/ListEmptyState';
import { ArticleCard, ArticleCardSkeleton } from '../ArticleCard';
import { cardSizeClass } from '../../../utils/cardSizeClass';
import classNames from 'classnames';
import { ArticleListProps, ArticleListVariant } from './contracts';

const emptyStateIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

const containerClassName = ({
  withPagination,
  lazyLoad,
  variant,
  listVariant,
}: Pick<ArticleListProps, 'withPagination' | 'lazyLoad' | 'variant' | 'listVariant'>) =>
  classNames('article-card-list-container', {
    'article-card-list-container--with-pagination': withPagination && !lazyLoad,
    'article-card-list-container--lazy-load': lazyLoad,
    'article-card-list-container--default': variant === ArticleVariant.Default,
    'article-card-list-container--text-side-by-side': variant === ArticleVariant.TextSideBySide,
    'article-card-list-container--two-column-grid': listVariant === ArticleListVariant.TwoColumnGrid,
  });

export type ArticleListUIProps = ArticleListProps & {
  paginatedArticles: PaginatedResponse<Article>;
  allArticles?: Article[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  lazyLoad?: boolean;
};

export default function ArticleListUI({
  title,
  paginatedArticles,
  allArticles,
  withPagination,
  lazyLoad = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  variant,
  listVariant,
  showDate,
  showAuthor,
  showImage,
  showSummary,
  showTag,
  imageObjectFit,
  columns = 1,
  size,
  prioritize = false,
}: ArticleListUIProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const articles = lazyLoad && allArticles ? allArticles : (paginatedArticles?.items ?? []);

  // Infinite scroll observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!lazyLoad) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [lazyLoad, handleIntersection]);

  if (articles.length === 0 && !isFetchingNextPage) {
    return <ListEmptyState id="article-list" title={title} message="No news found" icon={emptyStateIcon} />;
  }

  const sizeClass = cardSizeClass('article-card-list-container', size);
  const className = classNames(containerClassName({ withPagination, lazyLoad, variant, listVariant }), sizeClass);
  // A fixed size auto-fills by card width (via the SCSS size class) and overrides `columns`.
  const listStyle = sizeClass ? undefined : { gridTemplateColumns: `repeat(${columns}, 1fr)` };

  return (
    <div className={className} id="article-list">
      {title && <h3 className="article-card-list-container__title">{title}</h3>}
      <ul className="article-card-list-container__list" style={listStyle}>
        {articles.map((article, index) => {
          return (
            <li className="article-card-list-container__list-item" key={article.id}>
              <ArticleCard
                variant={variant}
                article={article}
                showDate={showDate}
                showAuthor={showAuthor}
                showImage={showImage}
                showSummary={showSummary}
                showTag={showTag}
                imageObjectFit={imageObjectFit}
                prioritize={prioritize && index === 0}
              />
            </li>
          );
        })}
        {lazyLoad &&
          isFetchingNextPage &&
          Array.from({ length: columns * 2 }).map((_, index) => (
            <li
              className="article-card-list-container__list-item article-card-list-container__list-item--skeleton"
              key={`skeleton-${index}`}
            >
              <ArticleCardSkeleton
                variant={variant}
                showImage={showImage}
                showTag={showTag}
                showDate={showDate}
                showAuthor={showAuthor}
                showSummary={showSummary}
              />
            </li>
          ))}
      </ul>
      {lazyLoad && <div ref={loadMoreRef} className="article-card-list-container__load-more-trigger" />}
      {withPagination && !lazyLoad && (
        <Pagination
          forcePage={paginatedArticles.pagination.currentPage - 1}
          pageCount={paginatedArticles.pagination.totalPages}
        />
      )}
    </div>
  );
}
